// guide-illustrations.js — shared capture-guide illustration library for the
// clinical-photo and biomarker (rPPG) steps. Plain script, no JSX, no deps.
//
//   window.OPDGuides = {
//     get(shotId)  → { svg: '<svg…>', caption: {en,hi}, hint: {en,hi} } | null
//     ids()        → array of covered shot ids
//     meanLuma(v)  → 0–255 mean luminance of a playing <video>, or null
//   }
//
// Covers every photo-plan shot id (server/opd/photo-plan.mjs) plus 'face-rppg'
// for the biomarker face scan. All artwork uses currentColor so the consumer
// sets the colour (white over the camera feed); a consistent 200×260 viewBox,
// shared stroke weights and 3–4 fill opacities keep the set reading as one
// designed family. Consumers size the svg via the `.gd-guide svg` CSS rule
// injected by the step components.
(function (g) {
  'use strict';

  // ── shared drawing vocabulary ──────────────────────────────
  var VB = '0 0 200 260';
  // outline stroke shared by every filled shape
  var OUT = 'stroke="currentColor" stroke-opacity=".75" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"';

  function svg(body) {
    return '<svg viewBox="' + VB + '" xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true">' + body + '</svg>';
  }
  function fillP(d, op) {
    return '<path d="' + d + '" fill="currentColor" fill-opacity="' + op + '" ' + OUT + '/>';
  }
  function ell(cx, cy, rx, ry, op, extra) {
    return '<ellipse cx="' + cx + '" cy="' + cy + '" rx="' + rx + '" ry="' + ry + '" fill="currentColor" fill-opacity="' + op + '" ' + OUT + (extra ? ' ' + extra : '') + '/>';
  }
  // dashed target zone with a soft glow underneath (two passes of one shape)
  function dash(shape) {
    return shape('stroke="currentColor" stroke-opacity=".18" stroke-width="10" fill="none" stroke-linecap="round"')
         + shape('stroke="currentColor" stroke-opacity=".95" stroke-width="3.5" fill="none" stroke-linecap="round" stroke-dasharray="8 8"');
  }
  // full-body dashed framing rect (front + lateral share it)
  var FRAME = dash(function (a) { return '<rect x="46" y="8" width="108" height="244" rx="22" ' + a + '/>'; });

  // head-and-shoulders bust (front view) shared by eyes / throat / face-rppg
  function bust(faceDetails) {
    return [
      // shoulders
      fillP('M34 252 C38 208 64 188 100 188 C136 188 162 208 166 252 Z', '.2'),
      // neck
      fillP('M88 150 L88 178 C88 186 112 186 112 178 L112 150 Z', '.24'),
      // head
      ell(100, 102, 46, 54, '.2'),
      // hair
      fillP('M54 100 C54 62 74 42 100 42 C126 42 146 62 146 100 C138 78 122 66 100 66 C78 66 62 78 54 100 Z', '.36'),
    ].join('') + (faceDetails || '');
  }

  // hands (back view, fingers up) for the nails shot
  function hand(palmX, tops, thumbCx, thumbRot) {
    var fingers = '', nails = '';
    for (var i = 0; i < 4; i++) {
      var fx = palmX + i * 14, top = tops[i];
      fingers += '<rect x="' + fx + '" y="' + top + '" width="12" height="' + (178 - top) + '" rx="6" fill="currentColor" fill-opacity=".2" ' + OUT + '/>';
      nails += '<ellipse cx="' + (fx + 6) + '" cy="' + (top + 10) + '" rx="4" ry="5.5" fill="currentColor" fill-opacity=".55"/>';
    }
    return fingers
      + '<rect x="' + palmX + '" y="162" width="54" height="58" rx="16" fill="currentColor" fill-opacity=".24" ' + OUT + '/>'
      + '<ellipse cx="' + thumbCx + '" cy="186" rx="9" ry="17" transform="rotate(' + thumbRot + ' ' + thumbCx + ' 186)" fill="currentColor" fill-opacity=".24" ' + OUT + '/>'
      + nails;
  }

  function corner(d) {
    return '<path d="' + d + '" fill="none" stroke="currentColor" stroke-opacity=".95" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>';
  }

  // ── the illustration set ───────────────────────────────────
  var GUIDES = {

    'body-front': {
      svg: svg([
        fillP('M84 32 C84 17 91 11 100 11 C109 11 116 17 116 32 C111 23 106 20 100 20 C94 20 89 23 84 32 Z', '.36'),
        ell(100, 35, 16, 19, '.2'),
        fillP('M94 52 L94 62 L106 62 L106 52 Z', '.24'),
        fillP('M100 60 C113 60 122 63 126 68 C130 73 132 82 133 92 L137 130 C138 136 134 140 130 140 C126.5 140 124.5 137.5 124 133 L121 102 L121 146 L117 244 L102 244 L100 172 L98 244 L83 244 L79 146 L79 102 L76 133 C75.5 137.5 73.5 140 70 140 C66 140 62 136 63 130 L67 92 C68 82 70 73 74 68 C78 63 87 60 100 60 Z', '.2'),
        fillP('M80 126 L120 126 L118 158 L103 158 L100 142 L97 158 L82 158 Z', '.14'),
      ].join('') + FRAME),
      caption: { en: 'Stand straight, face the camera — whole body in the frame', hi: 'सीधे खड़े हों, कैमरे की ओर मुख करें — पूरा शरीर फ़्रेम में' },
      hint: { en: 'Stand 2–3 steps back', hi: '2–3 कदम पीछे खड़े हों' },
    },

    'body-lateral': {
      svg: svg([
        fillP('M97 12 C109 12 117 21 117 33 C117 42 113 49 107 53 C111 44 111 30 106 23 C103 17 100 14 97 12 Z', '.36'),
        ell(97, 33, 17, 20, '.2'),
        fillP('M81 34 L73 41 L82 47 Z', '.22'),
        fillP('M95 56 C87 62 82 72 80 84 C78 98 80 112 84 124 C87 134 88 142 87 152 L83 204 C82 220 81 232 80 240 L70 246 L94 246 L96 208 L99 176 L102 208 L102 246 L126 246 L114 240 C113 226 112 210 112 196 L114 152 C117 136 118 118 116 102 C115 84 110 64 102 58 Z', '.2'),
        fillP('M97 66 C93 78 92 96 94 114 L97 132 C98 139 104 139 105 132 L107 110 C108 92 107 74 104 66 Z', '.34'),
      ].join('') + FRAME),
      caption: { en: 'Turn to your side — whole body in the frame', hi: 'करवट मुड़कर खड़े हों — पूरा शरीर फ़्रेम में' },
      hint: { en: 'Arms relaxed at your side', hi: 'हाथ आराम से बगल में रखें' },
    },

    eyes: {
      svg: svg(
        bust(
          '<path d="M68 86 C74 81 86 81 92 85" fill="none" ' + OUT + '/>'
          + '<path d="M108 85 C114 81 126 81 132 86" fill="none" ' + OUT + '/>'
          + ell(80, 100, 8, 5, '.5') + ell(120, 100, 8, 5, '.5')
          + '<path d="M100 104 L100 122 M88 140 C94 145 106 145 112 140" fill="none" ' + OUT + '/>'
        ) + dash(function (a) { return '<rect x="46" y="82" width="108" height="36" rx="18" ' + a + '/>'; })
      ),
      caption: { en: 'Both eyes open, look at the camera', hi: 'दोनों आँखें खोलें, कैमरे की ओर देखें' },
      hint: { en: 'Good light on the face', hi: 'चेहरे पर अच्छी रोशनी' },
    },

    ears: {
      svg: svg([
        fillP('M30 252 C42 212 70 198 108 198 C146 198 166 214 172 252 Z', '.2'),
        fillP('M92 156 L90 200 L126 200 L120 154 Z', '.24'),
        fillP('M104 46 C130 46 150 70 150 102 C150 130 134 152 108 156 C96 158 86 152 80 142 C74 140 70 137 70 133 C70 129 73 126 76 124 C72 112 72 96 78 82 C84 62 92 46 104 46 Z', '.2'),
        fillP('M104 46 C132 46 152 70 152 102 C152 122 144 140 130 150 C140 130 142 104 134 84 C128 66 118 52 104 46 Z', '.36'),
        ell(118, 110, 11, 17, '.4'),
        '<path d="M114 104 C120 100 124 108 119 114" fill="none" ' + OUT + '/>',
      ].join('') + dash(function (a) { return '<circle cx="118" cy="110" r="36" ' + a + '/>'; })),
      caption: { en: 'Turn your head — show one ear to the camera', hi: 'सिर घुमाएँ — एक कान कैमरे को दिखाएँ' },
      hint: { en: 'Pull hair away from the ear', hi: 'कान से बाल हटाएँ' },
    },

    nails: {
      svg: svg(
        hand(34, [118, 98, 90, 98], 27, 28)
        + hand(112, [98, 90, 98, 118], 173, -28)
        + dash(function (a) { return '<rect x="26" y="78" width="148" height="58" rx="26" ' + a + '/>'; })
      ),
      caption: { en: 'Show your fingernails — hands flat, fingers together', hi: 'नाखून दिखाएँ — हाथ सीधे, उँगलियाँ साथ' },
      hint: { en: 'Remove nail polish if any', hi: 'नेल पॉलिश हो तो हटाएँ' },
    },

    skin: {
      svg: svg([
        fillP('M28 214 C56 164 94 112 132 60 C138 50 156 58 150 70 C116 122 78 176 50 228 C42 240 20 228 28 214 Z', '.2'),
        ell(143, 62, 15, 13, '.24', 'transform="rotate(-40 143 62)"'),
        '<g fill="currentColor" fill-opacity=".5">'
          + '<circle cx="80" cy="136" r="3"/><circle cx="92" cy="128" r="4"/><circle cx="86" cy="148" r="3"/>'
          + '<circle cx="100" cy="140" r="4"/><circle cx="74" cy="150" r="3"/><circle cx="96" cy="156" r="4"/>'
          + '<circle cx="88" cy="118" r="3"/>'
          + '</g>',
      ].join('') + dash(function (a) { return '<circle cx="88" cy="140" r="38" ' + a + '/>'; })),
      caption: { en: 'Hold the camera close to the affected skin', hi: 'प्रभावित त्वचा के पास कैमरा रखें' },
      hint: { en: 'Hold steady so the photo stays sharp', hi: 'स्थिर रखें ताकि फ़ोटो साफ़ आए' },
    },

    scalp: {
      svg: svg([
        fillP('M34 252 C38 208 64 190 100 190 C136 190 162 208 166 252 Z', '.2'),
        fillP('M86 150 L86 196 L114 196 L114 150 Z', '.24'),
        ell(100, 100, 46, 54, '.2'),
        fillP('M100 46 C72 46 54 70 54 102 C54 130 70 152 100 156 C130 152 146 130 146 102 C146 70 128 46 100 46 Z', '.36'),
        fillP('M100 48 C97 76 97 108 100 136 C103 108 103 76 100 48 Z', '.6'),
      ].join('') + dash(function (a) { return '<ellipse cx="100" cy="84" rx="40" ry="30" ' + a + '/>'; })),
      caption: { en: 'Tilt your head — show the scalp area', hi: 'सिर झुकाएँ — सिर की त्वचा दिखाएँ' },
      hint: { en: 'Part the hair to show the skin', hi: 'बाल हटाकर त्वचा दिखाएँ' },
    },

    'affected-area': {
      svg: svg([
        // seated figure, side view: head, torso, thigh, shin, foot
        fillP('M58 28 C66 18 80 20 84 32 C80 28 72 27 66 30 C61 33 58 36 58 28 Z', '.36'),
        ell(70, 38, 14, 16, '.2'),
        '<rect x="52" y="58" width="40" height="74" rx="18" fill="currentColor" fill-opacity=".2" ' + OUT + '/>',
        '<rect x="56" y="118" width="94" height="30" rx="15" fill="currentColor" fill-opacity=".2" ' + OUT + '/>',
        '<rect x="122" y="138" width="28" height="84" rx="14" fill="currentColor" fill-opacity=".2" ' + OUT + '/>',
        '<rect x="118" y="214" width="54" height="18" rx="9" fill="currentColor" fill-opacity=".24" ' + OUT + '/>',
        // kneecap accent + swelling arcs
        ell(140, 134, 11, 12, '.34'),
        '<path d="M162 104 C169 111 172 120 170 130 M174 92 C184 102 188 116 184 130" fill="none" stroke="currentColor" stroke-opacity=".8" stroke-width="3" stroke-linecap="round"/>',
      ].join('') + dash(function (a) { return '<circle cx="140" cy="136" r="36" ' + a + '/>'; })),
      caption: { en: 'Centre the swelling or injury in the frame', hi: 'सूजन या चोट को फ़्रेम के बीच रखें' },
      hint: { en: 'Include the joint for size reference', hi: 'आकार के अंदाज़े के लिए जोड़ भी दिखाएँ' },
    },

    throat: {
      svg: svg(
        bust(
          '<path d="M70 96 C76 92 86 92 92 96 M108 96 C114 92 124 92 130 96" fill="none" ' + OUT + '/>'
          + ell(100, 134, 21, 17, '.12', 'stroke-opacity=".9"')
          + '<path d="M84 142 C90 152 110 152 116 142 C112 138 88 138 84 142 Z" fill="currentColor" fill-opacity=".35"/>'
          + '<circle cx="100" cy="126" r="3.5" fill="currentColor" fill-opacity=".5"/>'
        ) + dash(function (a) { return '<circle cx="100" cy="134" r="32" ' + a + '/>'; })
      ),
      caption: { en: 'Open wide — teeth, mouth and throat in view', hi: 'मुँह पूरा खोलें — दाँत, मुँह और गला दिखें' },
      hint: { en: 'Use torch light if it looks dark', hi: 'अंधेरा लगे तो टॉर्च जलाएँ' },
    },

    neck: {
      svg: svg(
        bust(
          // calm face, chin lifted: eyes as gentle arcs, head tilted up a touch
          '<path d="M72 92 C78 88 88 88 94 92 M106 92 C112 88 122 88 128 92" fill="none" ' + OUT + '/>'
          + '<path d="M92 136 C96 139 104 139 108 136" fill="none" ' + OUT + '/>'
          // subtle thyroid-area contour lines on the neck itself
          + '<path d="M90 162 C94 165 106 165 110 162 M88 170 C94 174 106 174 112 170" fill="none" stroke="currentColor" stroke-opacity=".45" stroke-width="2.5" stroke-linecap="round"/>'
        ) + dash(function (a) { return '<rect x="68" y="146" width="64" height="46" rx="20" ' + a + '/>'; })
      ),
      caption: { en: 'Lift your chin slightly — neck and throat area in view', hi: 'ठुड्डी थोड़ी ऊपर उठाएँ — गर्दन साफ़ दिखे' },
      hint: { en: 'Remove scarf / collar from the neck', hi: 'गर्दन से दुपट्टा / कॉलर हटाएँ' },
    },

    other: {
      svg: svg([
        corner('M40 62 L40 46 Q40 40 46 40 L62 40'),
        corner('M138 40 L154 40 Q160 40 160 46 L160 62'),
        corner('M160 198 L160 214 Q160 220 154 220 L138 220'),
        corner('M62 220 L46 220 Q40 220 40 214 L40 198'),
        fillP('M66 112 L82 112 L88 102 L112 102 L118 112 L134 112 Q142 112 142 120 L142 152 Q142 160 134 160 L66 160 Q58 160 58 152 L58 120 Q58 112 66 112 Z', '.2'),
        '<circle cx="100" cy="135" r="16" fill="none" ' + OUT + '/>',
        '<circle cx="100" cy="135" r="8" fill="currentColor" fill-opacity=".45"/>',
        '<circle cx="130" cy="121" r="3" fill="currentColor" fill-opacity=".5"/>',
      ].join('')),
      caption: { en: 'Anything else the doctor should see', hi: 'कुछ और जो डॉक्टर को देखना चाहिए' },
      hint: { en: 'One clear photo is enough', hi: 'एक साफ़ फ़ोटो काफ़ी है' },
    },

    'face-rppg': {
      svg: svg(
        bust(
          ell(80, 100, 6, 4, '.55') + ell(120, 100, 6, 4, '.55')
          + '<path d="M100 104 L100 120 M88 138 C94 144 106 144 112 138" fill="none" ' + OUT + '/>'
        )
        + '<path d="M52 224 L78 224 L86 208 L95 238 L104 214 L110 224 L148 224" fill="none" stroke="currentColor" stroke-opacity=".9" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>'
        + dash(function (a) { return '<ellipse cx="100" cy="104" rx="56" ry="66" ' + a + '/>'; })
      ),
      caption: { en: 'Keep your face inside the oval and stay still', hi: 'चेहरा अंडाकार के अंदर रखें और स्थिर रहें' },
      hint: { en: 'Good light on the face', hi: 'चेहरे पर अच्छी रोशनी' },
    },
  };

  // ── live-feedback helper: mean luminance of a playing video ─
  var lumaCanvas = null;
  function meanLuma(video) {
    try {
      if (!video || !video.videoWidth || !video.videoHeight) return null;
      if (!lumaCanvas) {
        lumaCanvas = document.createElement('canvas');
        lumaCanvas.width = 32; lumaCanvas.height = 24;
      }
      var ctx = lumaCanvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, 32, 24);
      var d = ctx.getImageData(0, 0, 32, 24).data;
      var sum = 0;
      for (var i = 0; i < d.length; i += 4) sum += 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
      return sum / (d.length / 4);
    } catch (e) { return null; }
  }

  g.OPDGuides = {
    get: function (shotId) { return GUIDES[shotId] || null; },
    ids: function () { return Object.keys(GUIDES); },
    meanLuma: meanLuma,
  };
})(typeof window !== 'undefined' ? window : globalThis);
